import React, { useEffect, useRef, useState } from 'react';

import {
  FocusedInput,
  OnDatesChangeProps,
  START_DATE,
  useDatepicker,
} from '@datepicker-react/hooks';
import classNames from 'classnames';
import { subDays, subMonths } from 'date-fns';

import { getFormatDate } from '../../../utils/utils';
import DatepickerContentWrapper from '../datepicker-content-wrapper/datepicker-content-wrapper';
import DatepickerContext from '../datepicker-context';

import DateIcon from './date-icon.svg';

import './datepicker.scss';

interface DatepickerProps {
  type: 'date' | 'range';
  onPeriodChange?: (range: { start: Date | null; end: Date | null }) => void;
  period?: { from: Date | null; to: Date | null };
  sm?: boolean;
  lg?: boolean;
  label?: string;
}

const Datepicker: React.FC<DatepickerProps> = ({
  type,
  onPeriodChange,
  sm,
  label,
  period,
}) => {
  const pickerWrapperRef = useRef<HTMLDivElement>(null);
  const [showCalendar, setShowCalendar] = useState(false);
  const [dateState, setDateState] = useState<{
    startDate: Date | null;
    endDate: Date | null;
    focusedInput: FocusedInput;
  }>({
    startDate: period && period.from ? new Date(period.from) : null,
    endDate: period && period.to ? new Date(period.to) : null,
    focusedInput: START_DATE,
  });
  const handleDateChange = (data: OnDatesChangeProps) => {
    setDateState(
      !data.focusedInput ? { ...data, focusedInput: START_DATE } : data
    );
  };
  const {
    firstDayOfWeek,
    activeMonths,
    isDateHovered,
    isFirstOrLastSelectedDate,
    isDateBlocked,
    isDateSelected,
    isDateFocused,
    focusedDate,
    onDateHover,
    onDateSelect,
    onDateFocus,
    goToDate,
  } = useDatepicker({
    startDate: dateState.startDate,
    endDate: dateState.endDate,
    focusedInput: dateState.focusedInput,
    onDatesChange: handleDateChange,
  });

  useEffect(() => {
    if (period) {
      setDateState((prevState) => ({
        ...prevState,
        startDate: period.from ? new Date(period.from) : null,
        endDate: period.to ? new Date(period.to) : null,
      }));
    }
  }, [period]);

  const setPresetRange = (
    type:
      | 'week'
      | 'month'
      | 'quarter-1'
      | 'quarter-2'
      | 'quarter-3'
      | 'quarter-4'
  ) => {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();

    switch (type) {
      case 'week':
        setDateState({
          startDate: subDays(currentDate, 7),
          endDate: currentDate,
          focusedInput: START_DATE,
        });
        goToDate(currentDate);
        break;
      case 'month':
        setDateState({
          startDate: subMonths(currentDate, 1),
          endDate: currentDate,
          focusedInput: START_DATE,
        });
        goToDate(currentDate);
        break;
      case 'quarter-1':
        setDateState({
          startDate: new Date(currentYear, 1, 1),
          endDate: new Date(currentYear, 3, 31),
          focusedInput: START_DATE,
        });
        goToDate(new Date(currentYear, 1, 1));
        break;
      case 'quarter-2':
        setDateState({
          startDate: new Date(currentYear, 4, 1),
          endDate: new Date(currentYear, 6, 30),
          focusedInput: START_DATE,
        });
        goToDate(new Date(currentYear, 4, 1));
        break;
      case 'quarter-3':
        setDateState({
          startDate: new Date(currentYear, 7, 1),
          endDate: new Date(currentYear, 9, 30),
          focusedInput: START_DATE,
        });
        goToDate(new Date(currentYear, 7, 1));
        break;
      case 'quarter-4':
        setDateState({
          startDate: new Date(currentYear, 10, 1),
          endDate: new Date(currentYear, 12, 31),
          focusedInput: START_DATE,
        });
        goToDate(new Date(currentYear, 10, 1));
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    const handleEscClick = (e: KeyboardEvent) => {
      if (showCalendar && e.code === 'Escape') {
        setShowCalendar(false);
        setDateState({
          startDate: null,
          endDate: null,
          focusedInput: START_DATE,
        });
      }
    };

    document.addEventListener('keydown', handleEscClick);

    return () => {
      document.removeEventListener('keydown', handleEscClick);
    };
  }, [pickerWrapperRef, showCalendar]);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (
        !(
          pickerWrapperRef.current &&
          pickerWrapperRef.current.contains(e.target as Node)
        )
      ) {
        if (showCalendar) {
          setShowCalendar(false);
          setDateState({
            startDate: null,
            endDate: null,
            focusedInput: START_DATE,
          });
        }
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => {
      document.removeEventListener('mousedown', handleClick);
    };
  }, [pickerWrapperRef, showCalendar]);

  const getLabel = () => {
    if (dateState.startDate || dateState.endDate)
      return `${
        dateState.startDate
          ? getFormatDate(dateState.startDate, 'dd MMM yyyy', true)
          : 'Не выбрано'
      } - ${
        dateState.endDate
          ? getFormatDate(dateState.endDate, 'dd MMM yyyy', true)
          : 'Не выбрано'
      }`;
    if (label) return label;
    return 'Выберите период';
  };

  return (
    <DatepickerContext.Provider
      value={{
        focusedDate,
        isDateFocused,
        isDateSelected,
        isDateHovered,
        isDateBlocked,
        isFirstOrLastSelectedDate,
        onDateSelect,
        onDateFocus,
        onDateHover,
      }}
    >
      <div
        ref={pickerWrapperRef}
        className={classNames('ref-container', {
          'ref-container--sm': sm,
          'ref-container--active': showCalendar,
          'ref-container--completed': dateState.startDate || dateState.endDate,
        })}
      >
        <div className="calendar__input">
          <div
            role="button"
            tabIndex={-1}
            onKeyPress={undefined}
            onClick={() => {
              setShowCalendar((prevState) => !prevState);
            }}
            className={classNames('input input--sm', {
              'input--active': showCalendar,
            })}
          >
            <input readOnly className="datepicker__input input__input" />
            <span className="datepicker-input__label">{getLabel()}</span>
            <div className="datepicker-icon">
              <img src={DateIcon} alt="" />
            </div>
          </div>
        </div>
        {showCalendar && (
          <DatepickerContentWrapper
            onSubmit={() => {
              if (onPeriodChange) {
                onPeriodChange({
                  start: dateState.startDate,
                  end: dateState.endDate,
                });
              } else {
                setDateState({
                  startDate: null,
                  endDate: null,
                  focusedInput: START_DATE,
                });
              }
              setShowCalendar(false);
            }}
            onCancel={() => {
              onPeriodChange &&
                onPeriodChange({
                  start: null,
                  end: null,
                });
              setDateState({
                startDate: null,
                endDate: null,
                focusedInput: START_DATE,
              });
            }}
            setPresetRange={setPresetRange}
            goToDate={goToDate}
            activeMonths={activeMonths}
            firstDayOfWeek={firstDayOfWeek}
            disabled={!dateState.startDate || !dateState.endDate}
          />
        )}
      </div>
    </DatepickerContext.Provider>
  );
};

export default Datepicker;
